import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { RECRUITMENT_SOURCE_INFO } from '@/constants/recruitmentSources'
import { RECRUITMENT_SOURCES } from '@/constants/recruitment'
import type { RecruitmentSource } from '@/types'
import { cn } from '@/lib/utils'

interface RecruitmentSourceCardProps {
  source: RecruitmentSource
  onScout: (source: RecruitmentSource) => void
  disabled?: boolean
  /** Current available AP for the party */
  availableAP?: number
  className?: string
}

export function RecruitmentSourceCard({
  source,
  onScout,
  disabled = false,
  availableAP,
  className,
}: RecruitmentSourceCardProps) {
  const info = RECRUITMENT_SOURCE_INFO[source]
  const sourceConfig = RECRUITMENT_SOURCES[source]
  const icon = sourceConfig.icon
  const apCost = sourceConfig.apCost

  // Disable if explicitly disabled or not enough AP
  const insufficientAP = availableAP !== undefined && availableAP < apCost
  const isDisabled = disabled || insufficientAP

  return (
    <Card className={cn('flex flex-col', insufficientAP && 'opacity-60', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl" role="img" aria-label={info.name}>
              {icon}
            </span>
            <CardTitle className="text-base">{info.name}</CardTitle>
          </div>
          <Badge variant={insufficientAP ? 'secondary' : 'outline'} className="shrink-0">
            {apCost} AP
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 pb-3">
        <p className="text-muted-foreground text-sm">{info.description}</p>

        <div className="space-y-1">
          {info.strengths.map((strength, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <span className="text-green-500">✓</span>
              <span className="text-green-700 dark:text-green-400">{strength}</span>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          {info.weaknesses.map((weakness, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <span className="text-amber-500">✗</span>
              <span className="text-amber-700 dark:text-amber-400">{weakness}</span>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="pt-0">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => onScout(source)}
          disabled={isDisabled}
        >
          {insufficientAP ? 'Not enough AP' : 'Scout'}
        </Button>
      </CardFooter>
    </Card>
  )
}
